import { MutationTree } from 'vuex';
import {
    TaskChecklistDeleteRequestPayload, TaskChecklistItemMarkRequestPayload, TaskChecklistItemResourcePayload,
    TaskChecklistResourcePayload,
    TaskCommentDeleteRequestPayload,
    TaskCommentResourcePayload, TaskMediaDeleteRequestPayload, TaskMediaResourcePayload,
    TaskState,
} from './types';
import { TaskResource } from '@/api/requests/work/types';
import { SingleFilter } from '@/store/types';
import Vue from 'vue';
import moment from 'moment';

export const mutations: MutationTree<TaskState> = {
    /**
     * Add a task to the list.
     *
     * @param state
     * @param task
     */
    addTask(state, task: TaskResource) {
        state.tasks.push(task);
    },

    /**
     * Add a checklist to a task, or update if already exists.
     *
     * @param state
     * @param taskId
     * @param checklist
     */
    addChecklistToTask(state, { taskId, checklist }: TaskChecklistResourcePayload) {
        const task = state.tasks.find((item) => item.id === taskId);
        if (!task) {
            return;
        }
        const index = task && task.checklists.length > 0
            ? task.checklists.findIndex((item) => item.id === checklist.id)
            : -1;
        if (index > -1) {
            task.checklists.splice(index, 1, checklist);
        } else {
            task.checklists.push(checklist);
        }
    },

    /**
     * Remove a checklist from a task.
     *
     * @param state
     * @param taskId
     * @param checklistId
     */
    removeChecklistFromTask(state, { taskId, checklistId }: TaskChecklistDeleteRequestPayload) {
        const task = state.tasks.find((item) => item.id === taskId);
        if (!task) {
            return;
        }
        const index = task && task.checklists.length > 0
            ? task.checklists.findIndex((item) => item.id === checklistId)
            : -1;
        if (index > -1) {
            task.checklists.splice(index, 1);
        }
    },

    /**
     * Add a checklist item to a task, or update if already exists.
     *
     * @param state
     * @param taskId
     * @param checklistId
     * @param item
     */
    addChecklistItemToTask(state, { taskId, checklistId, item }: TaskChecklistItemResourcePayload) {
        const task = state.tasks.find((value) => value.id === taskId);
        if (!task) {
            return;
        }
        const checklist = task.checklists.find((value) => value.id === checklistId);
        if (!checklist) {
            return;
        }
        const index = checklist.items.length > 0
            ? checklist.items.findIndex((value) => value.id === item.id)
            : -1;
        if (index > -1) {
            checklist.items.splice(index, 1, item);
        } else {
            checklist.items.push(item);
        }
    },

    /**
     * Remove a checklist item from a task.
     *
     * @param state
     * @param taskId
     * @param checklistId
     * @param itemId
     */
    removeChecklistItemFromTask(state, { taskId, checklistId, itemId }: TaskChecklistItemMarkRequestPayload) {
        const task = state.tasks.find((value) => value.id === taskId);
        if (!task) {
            return;
        }
        const checklist = task.checklists.find((value) => value.id === checklistId);
        if (!checklist) {
            return;
        }
        const index = checklist.items.length > 0
            ? checklist.items.findIndex((value) => value.id === itemId)
            : -1;
        if (index > -1) {
            checklist.items.splice(index, 1);
        }
    },

    /**
     * Add a comment to a task, or update if already exists.
     *
     * @param state
     * @param taskId
     * @param comment
     */
    addCommentToTask(state, { taskId, comment }: TaskCommentResourcePayload) {
        const task = state.tasks.find((item) => item.id === taskId);
        if (!task) {
            return;
        }
        const index = task && task.comments.length > 0
            ? task.comments.findIndex((item) => item.id === comment.id)
            : -1;
        if (index > -1) {
            task.comments.splice(index, 1, comment);
        } else {
            task.comments.push(comment);
        }
    },

    /**
     * Remove a comment from a task.
     *
     * @param state
     * @param taskId
     * @param commentId
     */
    removeCommentFromTask(state, { taskId, commentId }: TaskCommentDeleteRequestPayload) {
        const task = state.tasks.find((item) => item.id === taskId);
        if (!task) {
            return;
        }
        const index = task && task.comments.length > 0
            ? task.comments.findIndex((item) => item.id === commentId)
            : -1;
        if (index > -1) {
            task.comments.splice(index, 1);
        }
    },

    /**
     * Add a media to a task, or update if already exists.
     *
     * @param state
     * @param taskId
     * @param media
     */
    addMediaToTask(state, { taskId, media }: TaskMediaResourcePayload) {
        const task = state.tasks.find((item) => item.id === taskId);
        if (!task) {
            return;
        }
        const index = task && task.media.length > 0
            ? task.media.findIndex((item) => item.id === media.id)
            : -1;
        if (index > -1) {
            task.media.splice(index, 1, media);
        } else {
            task.media.push(media);
        }
    },

    /**
     * Remove a media from a task.
     *
     * @param state
     * @param taskId
     * @param mediaId
     */
    removeMediaFromTask(state, { taskId, mediaId }: TaskMediaDeleteRequestPayload) {
        const task = state.tasks.find((item) => item.id === taskId);
        if (!task) {
            return;
        }
        const index = task && task.media.length > 0
            ? task.media.findIndex((item) => item.id === mediaId)
            : -1;
        if (index > -1) {
            task.media.splice(index, 1);
        }
    },

    /**
     * Remove a task from the list.
     *
     * @param state
     * @param taskId
     */
    removeTask(state, taskId: number) {
        state.tasks = state.tasks.filter((task) => task.id !== taskId);
    },

    /**
     * Reset the active task id.
     *
     * @param state
     */
    resetActiveTaskId(state) {
        state.activeTaskId = null;
    },

    /**
     * Reset a single filter.
     *
     * @param state
     * @param key
     */
    resetFilter(state, key: string) {
        delete state.filters[key];
    },

    /**
     * Reset query filters.
     *
     * @param state
     */
    resetFilters(state) {
        state.filters = {};
    },

    /**
     * Reset the task list.
     *
     * @param state
     */
    resetTasks(state) {
        state.tasks = [];
    },

    /**
     * Set the active task id.
     *
     * @param state
     * @param id
     */
    setActiveTaskId(state, id: number) {
        state.activeTaskId = id;
    },

    /**
     * Set a query filter in state.
     *
     * @param state
     * @param key
     * @param value
     */
    setFilter(state, { key, value }: SingleFilter) {
        state.filters = { ...state.filters, [key]: value };
    },

    /**
     * Set active filters in calendar
     *
     * @param state
     * @param filters
     */
    setFilters(state, filters: any) {
        Vue.set(state.filters, 'start', filters && filters.start ? moment(filters.start) : undefined);
        Vue.set(state.filters, 'end', filters && filters.end ? moment(filters.end) : undefined);
        Vue.set(state.filters, 'work_id', filters && filters.work_id ? filters.work_id : undefined);
    },

    /**
     * Set the task list.
     *
     * @param state
     * @param tasks
     */
    setTasks(state, tasks: TaskResource[]) {
        state.tasks = tasks;
    },


    /**
     * Set the with event stutus.
     *
     * @param state
     * @param id
     */
    setWithEventStatus(state, status: boolean) {
        state.withEvent = status;
    },

    /**
     * Update a task.
     *
     * @param state
     * @param task
     */
    updateTask(state, task: TaskResource) {
        const index = state.tasks.findIndex((item) => item.id === task.id);
        state.tasks.splice(index, 1, task);
    },

    /**
     * Update a task.
     *
     * @param state
     * @param task
     */
    upsertTask(state, task: TaskResource) {
        const index = state.tasks.findIndex((item) => item.id === task.id);
        if (index > -1) {
            state.tasks.splice(index, 1, task);
        } else {
            state.tasks.push(task);
        }
    },
};
